import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Link,
  Select,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React, { useMemo, useRef, useState } from "react";
import Geosuggest from "react-geosuggest";
import { CherryPayApi } from "../../../api/models";
import {
  AU_STATES,
  buildManualGeocodedAddress,
  emptyManualAddressParts,
  getCountryDisplayName,
  getManualAddressValidationError,
  hasAnyManualAddressInput,
  hasCompleteManualParts,
  isManualGeocodedAddress,
  ManualAddressParts,
  normalizeCountryCode,
  parseManualPartsFromGeocodedAddress,
  sanitizePostcodeInput,
  splitAddressLines,
} from "../../../util/buildManualGeocodedAddress";
import { BaseFieldProps, FieldWrapper } from "../FieldWrapper/FieldWrapper";

interface GeocodedAddressProps extends BaseFieldProps {
  country: string | string[] | undefined;
  placeholder?: string | undefined;
}

function getDefaultCountryCode(country: string | string[] | undefined): string {
  if (Array.isArray(country)) {
    return normalizeCountryCode(country[0] || "au");
  }
  return normalizeCountryCode(country || "au");
}

function getCountryOptions(country: string | string[] | undefined): string[] {
  if (Array.isArray(country) && country.length > 0) {
    return country.map((code) => normalizeCountryCode(code));
  }
  if (typeof country === "string" && country.trim()) {
    return [normalizeCountryCode(country)];
  }
  return ["AU"];
}

function hasPlacesComponent(components: any[], type: string): boolean {
  return components.some(
    (c) =>
      c.types?.includes(type) ||
      (type === "locality" && c.types?.includes("postal_town"))
  );
}

const PLACES_REQUIRED = [
  "street_number",
  "route",
  "locality",
  "administrative_area_level_1",
  "postal_code",
  "country",
] as const;

const PLACES_LABELS: Record<typeof PLACES_REQUIRED[number], string> = {
  street_number: "street number",
  route: "route",
  locality: "locality",
  administrative_area_level_1: "state",
  postal_code: "post code",
  country: "country",
};

export function GeocodedAddress({
  name,
  label,
  id,
  helperText,
  infoText,
  country,
  placeholder,
}: GeocodedAddressProps) {
  const defaultCountryCode = getDefaultCountryCode(country);
  const countryOptions = useMemo(() => getCountryOptions(country), [country]);
  const [isManualMode, setIsManualMode] = useState(false);
  const [manualParts, setManualParts] = useState<ManualAddressParts>(() =>
    emptyManualAddressParts(defaultCountryCode)
  );
  const [addressText, setAddressText] = useState("");

  const isManualModeRef = useRef(isManualMode);
  const manualPartsRef = useRef(manualParts);
  const addressTextRef = useRef(addressText);
  isManualModeRef.current = isManualMode;
  manualPartsRef.current = manualParts;
  addressTextRef.current = addressText;

  const validateAddress = (
    providedValue: CherryPayApi.GeocodedAddress | null
  ): string | boolean => {
    if (!providedValue) {
      return true;
    }

    const addressComponent = providedValue.address_components;
    if (!addressComponent) {
      return "You must select or enter a valid address";
    }

    if (isManualGeocodedAddress(providedValue)) {
      return (
        getManualAddressValidationError(
          parseManualPartsFromGeocodedAddress(
            providedValue as any,
            defaultCountryCode
          )
        ) ?? true
      );
    }

    const missing = PLACES_REQUIRED.filter(
      (type) => !hasPlacesComponent(addressComponent, type)
    ).map((type) => PLACES_LABELS[type]);

    return missing.length
      ? `You must select an address which includes a ${missing.join(", ")}`
      : true;
  };

  const applyValidatedValue = (
    form: { setFieldValue: Function; setFieldError: Function },
    fieldName: string,
    value: CherryPayApi.GeocodedAddress | null
  ) => {
    const result = validateAddress(value);
    if (result === true) {
      form.setFieldValue(fieldName, value, true);
    } else {
      form.setFieldError(fieldName, result as string);
    }
  };

  return (
    <FieldWrapper
      name={name}
      id={id}
      label={label}
      helperText={helperText}
      infoText={infoText}
      validate={(providedValue) => {
        const parts = manualPartsRef.current;
        if (
          isManualModeRef.current &&
          (hasAnyManualAddressInput(parts) || addressTextRef.current.trim())
        ) {
          return getManualAddressValidationError(parts) ?? undefined;
        }
        const result = validateAddress(providedValue);
        return result === true ? undefined : (result as string);
      }}
    >
      {({ field, form }) => {
        const resetManualState = () => {
          const empty = emptyManualAddressParts(defaultCountryCode);
          manualPartsRef.current = empty;
          addressTextRef.current = "";
          setManualParts(empty);
          setAddressText("");
        };

        const switchToManual = () => {
          form.setFieldValue(field.name, null, true);
          resetManualState();
          isManualModeRef.current = true;
          setIsManualMode(true);
        };

        const switchToAutocomplete = () => {
          form.setFieldValue(field.name, null, true);
          resetManualState();
          isManualModeRef.current = false;
          setIsManualMode(false);
        };

        const clearManualFields = () => {
          form.setFieldValue(field.name, null, true);
          resetManualState();
        };

        const updateManualParts = (nextParts: ManualAddressParts) => {
          manualPartsRef.current = nextParts;
          setManualParts(nextParts);

          if (hasCompleteManualParts(nextParts)) {
            const synthesized = buildManualGeocodedAddress(
              nextParts
            ) as CherryPayApi.GeocodedAddress;
            applyValidatedValue(form, field.name, synthesized);
          } else if (field.value) {
            form.setFieldValue(field.name, null, true);
          }
        };

        const updateAddressText = (text: string) => {
          addressTextRef.current = text;
          setAddressText(text);
          updateManualParts({
            ...manualPartsRef.current,
            ...splitAddressLines(text),
          });
        };

        const linkProps = {
          textDecor: "underline" as const,
          color: "blue.500",
          fontSize: "sm",
          as: "a" as const,
        };

        return (
          <Box w="100%">
            {field.value && !isManualMode && (
              <Text fontWeight="bold">
                {field.value?.formatted_address}{" "}
                <Link
                  {...linkProps}
                  ms="10px"
                  onClick={() => {
                    isManualModeRef.current = false;
                    setIsManualMode(false);
                    form.setFieldValue(field.name, null, true);
                  }}
                >
                  change
                </Link>
              </Text>
            )}

            {!field.value && !isManualMode && (
              <Box>
                <Geosuggest
                  width="100%"
                  types={["address"]}
                  onSuggestSelect={(val: any) => {
                    let value: CherryPayApi.GeocodedAddress | null;

                    if (val?.gmaps?.address_components) {
                      value = {
                        address_components: val.gmaps.address_components,
                        formatted_address: val.gmaps.formatted_address,
                        geometry: val.gmaps.geometry,
                      };
                      if (value.geometry) {
                        delete (value.geometry as any)?.viewport;
                      }
                    } else {
                      value = null;
                    }

                    applyValidatedValue(form, field.name, value);
                  }}
                  autoFocus
                  country={country}
                  placeholder={placeholder}
                />
                <Link
                  mt="8px"
                  display="inline-block"
                  {...linkProps}
                  onClick={switchToManual}
                >
                  Can&apos;t find your address?
                </Link>
              </Box>
            )}

            {isManualMode && (
              <Box>
                <Flex direction="column" gap={3}>
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1}>
                      Address
                    </FormLabel>
                    <Textarea
                      minH="88px"
                      autoComplete="off"
                      value={addressText}
                      onChange={(e) => updateAddressText(e.target.value)}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" mb={1}>
                      Suburb
                    </FormLabel>
                    <Input
                      value={manualParts.suburb}
                      onChange={(e) =>
                        updateManualParts({
                          ...manualParts,
                          suburb: e.target.value,
                        })
                      }
                    />
                  </FormControl>

                  <Flex direction={{ base: "column", md: "row" }} gap={3}>
                    <FormControl flex="1">
                      <FormLabel fontSize="sm" mb={1}>
                        State
                      </FormLabel>
                      <Select
                        placeholder="Select state"
                        value={manualParts.state}
                        onChange={(e) =>
                          updateManualParts({
                            ...manualParts,
                            state: e.target.value,
                          })
                        }
                      >
                        {AU_STATES.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl flex="1">
                      <FormLabel fontSize="sm" mb={1}>
                        Postcode
                      </FormLabel>
                      <Input
                        inputMode="numeric"
                        value={manualParts.postcode}
                        onChange={(e) =>
                          updateManualParts({
                            ...manualParts,
                            postcode: sanitizePostcodeInput(e.target.value),
                          })
                        }
                      />
                    </FormControl>
                  </Flex>

                  <FormControl>
                    <FormLabel fontSize="sm" mb={1}>
                      Country
                    </FormLabel>
                    {countryOptions.length === 1 ? (
                      <Input
                        value={getCountryDisplayName(countryOptions[0])}
                        isReadOnly
                        backgroundColor="gray.50"
                      />
                    ) : (
                      <Select
                        value={manualParts.countryCode}
                        onChange={(e) =>
                          updateManualParts({
                            ...manualParts,
                            countryCode: e.target.value,
                          })
                        }
                      >
                        {countryOptions.map((code) => (
                          <option key={code} value={code}>
                            {getCountryDisplayName(code)}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                </Flex>

                <Flex mt="8px" justify="space-between" align="center">
                  <Link {...linkProps} onClick={switchToAutocomplete}>
                    Search address
                  </Link>
                  <Link {...linkProps} onClick={clearManualFields}>
                    Clear
                  </Link>
                </Flex>
              </Box>
            )}
          </Box>
        );
      }}
    </FieldWrapper>
  );
}
